import { ApiError, type Problem } from "./ApiError";

// Cliente HTTP tipado mínimo sobre fetch. Las formas de request/response se
// tipan en cada llamada contra los tipos de src/core/types (derivados del
// contrato en api/contracts/openapi.yaml) — este módulo no redeclara el
// contrato, solo transporta.
//
// TODO(implement-spec siguiente que consuma un endpoint real): correr
// openapi-typescript sobre api/contracts/openapi.yaml y publicar el tipo
// generado en src/core/types/openapi.d.ts, como hace api/ con
// `contract:types` (ADR-004). SPEC-201 no consume ningún endpoint de negocio
// más que /v1/portal/session, así que ese generador no se cableó todavía:
// dejarlo pendiente es una decisión explícita, no un olvido.

// SPEC-003 §4: la credencial del portal es exclusivamente una cookie HttpOnly.
// El secreto CSRF no es una credencial y se conserva sólo en memoria: tras un
// reload GET /v1/portal/session lo rehidrata desde la respuesta del contrato.
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  // SPEC-204: `Idempotency-Key` es `required: true` en toda operación que
  // mueve saldo o consume un código (00-base.yaml:526-532) —
  // `express-openapi-validator` la exige antes del controlador
  // (points.controller.ts:72-77). Un campo acotado, no un `headers:
  // Record<string,string>` genérico: eso abriría la puerta a que cualquier
  // pantalla fije cualquier cabecera, cuando el contrato exige exactamente
  // esta una. El validador también la va a necesitar para consumir un código
  // QR (SPEC-303/304), por eso vive en `core` y no en `portal`.
  idempotencyKey?: string;
  // SPEC-301: el validador se autentica con bearer JWT
  // (`validatorSession`, `api/contracts/parts/00-base.yaml:509-514`), no con
  // la cookie HttpOnly + CSRF del portal. Mismo criterio que
  // `idempotencyKey` arriba: un campo acotado que transporta exactamente lo
  // que el contrato exige, no un `headers: Record<string,string>` generico.
  // Donde vive el token (localStorage, memoria) y como se decide "mantener
  // la sesion iniciada" es responsabilidad de quien arma cada request
  // (`web/src/validator/**`) - `core` solo lo copia a `Authorization` si se
  // lo pasan; no sabe de donde salio ni lo persiste.
  bearerToken?: string;
}

export interface ListMeta {
  next: string | null;
  total: number;
}

export interface Envelope<T> {
  ok: true;
  data: T;
  message?: string;
  meta?: ListMeta;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

// Devuelve el sobre completo (ADR-004): úsala cuando la pantalla necesita
// `message` (confirmaciones) o `meta` (listados paginados). `apiFetch` sigue
// siendo el atajo para el caso común, que solo quiere `data`.
export async function apiFetchEnvelope<T>(
  path: string,
  options: RequestOptions = {},
): Promise<Envelope<T>> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const method = options.method ?? "GET";
  if (method !== "GET" && csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }
  if (options.idempotencyKey) {
    headers["Idempotency-Key"] = options.idempotencyKey;
  }
  if (options.bearerToken) {
    headers["Authorization"] = `Bearer ${options.bearerToken}`;
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }

  const response = await fetch(`${API_BASE}${path}`, init);

  if (!response.ok) {
    let problem: Problem | undefined;
    try {
      problem = (await response.json()) as Problem;
    } catch {
      problem = undefined;
    }
    throw new ApiError(response.status, problem);
  }

  if (response.status === 204) {
    return { ok: true, data: undefined as T };
  }
  return (await response.json()) as Envelope<T>;
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const envelope = await apiFetchEnvelope<T>(path, options);
  return envelope.data;
}
